import { FC, useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import { consentRequiredRegions } from '../../modules/Constants';

declare global {
	interface Window {
		clarity?: (...args: unknown[]) => void;
	}
}

type RenderClarityProps = {
	isSiteLoaded: boolean;
	uid?: string;
	imid?: string;
	countryCode?: string | null;
};

// Microsoft Clarity session recording. Runs in production only: NEXT_PUBLIC_CLARITY_ID
// is set solely in .env.prod, so dev/int/acpt builds render nothing and never send
// recordings. Mirrors the TopHotelPrices RenderClarity pattern (datadirects/TopHotelPrices#132).
const RenderClarity: FC<RenderClarityProps> = ({ isSiteLoaded, uid, imid, countryCode }) => {
	const clarityId = process.env.NEXT_PUBLIC_CLARITY_ID;
	const [isClarityReady, setIsClarityReady] = useState(false);
	const hasIdentified = useRef(false);
	const hasConsented = useRef(false);

	// Poll until the Clarity command queue exists (the inline snippet defines it
	// asynchronously), mirroring RenderGA's gtag readiness discipline.
	useEffect(() => {
		if (!clarityId || !isSiteLoaded || isClarityReady) return;

		let cancelled = false;
		const check = () => {
			if (cancelled) return;
			if (typeof window.clarity !== 'undefined') {
				setIsClarityReady(true);
			} else {
				setTimeout(check, 100);
			}
		};
		check();

		return () => {
			cancelled = true;
		};
	}, [clarityId, isSiteLoaded, isClarityReady]);

	useEffect(() => {
		if (!isClarityReady || typeof window.clarity === 'undefined') return;

		// Same identity GA uses as user_id and FB as external_id (uid+imid from init()),
		// so Clarity recordings join to the GA session/user in BQ. uid/imid arrive async
		// from init(); identify may legally fire mid-session, so we just wait for them.
		if (!hasIdentified.current && uid && imid) {
			hasIdentified.current = true;
			window.clarity('identify', `${uid}${imid}`);
		}

		// Consent mirrors RenderGA's Consent Mode posture: outside consentRequiredRegions
		// (EEA/UK/CH) storage is granted by default, so grant Clarity cookies explicitly.
		// Within those regions we make NO call — the Clarity dashboard Consent Mode
		// integration follows the gtag analytics_storage signal that the Cookiebot
		// bridge in RenderGA manages (denied until the user opts in). Recording itself
		// starts regardless: cookieless mode still stitches via identify above.
		if (!hasConsented.current && countryCode && !consentRequiredRegions.includes(countryCode)) {
			hasConsented.current = true;
			window.clarity('consent');
		}
	}, [isClarityReady, uid, imid, countryCode]);

	if (!clarityId || !isSiteLoaded) return null;

	// isSiteLoaded gate = same LCP protection as the marketing pixels in RenderGA.
	return (
		<Script id="ms-clarity" strategy="afterInteractive">
			{`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${clarityId}");`}
		</Script>
	);
};

export default RenderClarity;
